<template>
  <b-card no-body class="mb-4">
    <template #header>
      <div class="d-flex align-items-center justify-content-between">
        <span><i class="fas fa-chart-line mr-2"></i>รายงานการแสดงโฆษณา</span>
        <b-button
          size="sm"
          variant="outline-success"
          :disabled="loading || rows.length === 0"
          @click="exportExcel"
        >
          <i class="fas fa-file-excel mr-1"></i> Export
        </b-button>
      </div>
    </template>

    <div class="p-3">
      <b-row class="align-items-end">
        <b-col md="3">
          <b-form-group label="ตั้งแต่วันที่" class="mb-2">
            <b-form-input v-model="from" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="ถึงวันที่" class="mb-2">
            <b-form-input v-model="to" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="แยกตาม" class="mb-2">
            <b-form-checkbox-group v-model="groupBy" :options="groupOptions" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-button variant="primary" block class="mb-2" :disabled="loading" @click="load">
            <i class="fas fa-search mr-1" :class="{ 'fa-spin fa-spinner': loading }"></i>
            ดูรายงาน
          </b-button>
        </b-col>
      </b-row>

      <b-alert v-if="error" variant="danger" show class="mb-2">{{ error }}</b-alert>

      <b-alert variant="light" show class="small mb-3 border">
        <strong>ผู้ใช้ที่เห็น (รายวัน)</strong> นับผู้ใช้ซ้ำได้ถ้าเห็นคนละวัน
        ส่วน <strong>ผู้ใช้ไม่ซ้ำ</strong> นับหนึ่งคนครั้งเดียวตลอดช่วงที่เลือก
        — ตัวเลขสองค่านี้ไม่เท่ากันและใช้แทนกันไม่ได้
      </b-alert>
    </div>

    <b-table
      id="ads-report-table"
      :items="rows"
      :fields="fields"
      :busy="loading"
      responsive
      hover
      show-empty
      empty-text="ไม่มีข้อมูลในช่วงเวลาที่เลือก"
      class="mb-0"
    >
      <template #cell(impressions)="row">
        <span class="text-right d-block">{{ row.value.toLocaleString() }}</span>
      </template>
      <template #cell(dailyReach)="row">
        <span class="text-right d-block">{{ row.value.toLocaleString() }}</span>
      </template>
      <template #cell(uniqueUsers)="row">
        <span class="text-right d-block">{{ row.value.toLocaleString() }}</span>
      </template>
      <template #cell(clicks)="row">
        <span class="text-right d-block">{{ row.value.toLocaleString() }}</span>
      </template>
      <template #cell(ctr)="row">
        <span class="text-right d-block">{{ (row.value * 100).toFixed(2) }}%</span>
      </template>

      <template #custom-foot v-if="rows.length">
        <b-tr>
          <b-th :colspan="labelColumnCount">รวม</b-th>
          <b-th class="text-right">{{ totals.impressions.toLocaleString() }}</b-th>
          <b-th class="text-right">{{ totals.dailyReach.toLocaleString() }}</b-th>
          <b-th class="text-right text-muted">—</b-th>
          <b-th class="text-right">{{ totals.clicks.toLocaleString() }}</b-th>
          <b-th class="text-right">{{ (totals.ctr * 100).toFixed(2) }}%</b-th>
        </b-tr>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import AdsApiService from '@/services/AdsApiService';
import { exportTableToExcel } from '@/utils/reportExport';

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

export default {
  name: 'AdsReportPanel',
  data() {
    return {
      from: isoDaysAgo(29),
      to: isoDaysAgo(0),
      groupBy: [],
      rows: [],
      totals: { impressions: 0, clicks: 0, dailyReach: 0, ctr: 0 },
      loading: false,
      error: null,
      groupOptions: [
        { value: 'day', text: 'วัน' },
        { value: 'placement', text: 'ตำแหน่ง' },
        { value: 'branch', text: 'สาขา' },
        { value: 'role', text: 'บทบาท' }
      ]
    };
  },
  computed: {
    /** Label columns come before the numeric ones, so the totals row lines up. */
    labelColumnCount() {
      return 1 + this.groupBy.length;
    },
    fields() {
      const groupFields = this.groupBy.map((dimension) => ({
        day: { key: 'day', label: 'วัน' },
        placement: { key: 'placement', label: 'ตำแหน่ง' },
        branch: { key: 'branch', label: 'สาขา' },
        role: { key: 'role', label: 'บทบาท' }
      }[dimension]));

      return [
        { key: 'title', label: 'โฆษณา' },
        ...groupFields,
        { key: 'impressions', label: 'จำนวนครั้งที่แสดง', class: 'text-right' },
        { key: 'dailyReach', label: 'ผู้ใช้ที่เห็น (รายวัน)', class: 'text-right' },
        { key: 'uniqueUsers', label: 'ผู้ใช้ไม่ซ้ำ', class: 'text-right' },
        { key: 'clicks', label: 'คลิก', class: 'text-right' },
        { key: 'ctr', label: 'CTR', class: 'text-right' }
      ];
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const response = await AdsApiService.getAdReport({
          from: this.from,
          to: this.to,
          groupBy: this.groupBy
        });
        const data = response?.data?.data || {};
        this.rows = (data.rows || []).map((row) => ({
          ...row,
          title: row.title || `(ไม่มีชื่อ #${row.adId})`
        }));
        this.totals = {
          impressions: 0,
          clicks: 0,
          dailyReach: 0,
          ctr: 0,
          ...(data.totals || {})
        };
        this.$emit('loaded', this.rows);
      } catch (error) {
        // A 400 here is usually the 92-day cap, and the server says so.
        this.error =
          error?.response?.data?.message || 'โหลดรายงานไม่สำเร็จ';
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },

    exportExcel() {
      exportTableToExcel({
        tableId: 'ads-report-table',
        reportTitle: 'รายงานการแสดงโฆษณา',
        reportNameEn: 'ads-engagement',
        dateFrom: this.from,
        dateTo: this.to,
        sheetName: 'Ads'
      });
    }
  }
};
</script>
